import * as Sentry from '@sentry/react'
import { makeAutoObservable } from 'mobx'
import Loader from 'reusable/mobx-helpers/Loader'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import { apiClient } from 'api/trpc/trpc-client'
import { snackbarError } from 'reusable/mui/snackbar/snackbar'
import { notNull } from '@gap/utils/utils'
import { XeroConsentDto } from '@gap/backend/app-services/xero/models/XeroConsentDto'
import variables from 'variables/variables'
import OrgGuardStore from 'features/main/main-router/org-guard/OrgGuardStore'
import XpmConnectionStore, { XPM_CALLBACK_URL_KEY } from './XpmConnectionStore'

export type XeroOriginType = 'app' | 'trial'

const XPM_RETURN_PATH_KEY = 'xpm-return-path'

interface XeroXpmConectStoreProps {
   originType: XeroOriginType
   orgGuardStore: OrgGuardStore
   xpmConnectionStore: XpmConnectionStore
}

/** Per-surface card behaviour for the XPM integration; connection state lives in XpmConnectionStore. */
export default class XeroXpmConectStore {
   loader = new Loader(false)
   consent: XeroConsentDto | undefined = undefined
   redirecting = false
   sp

   constructor(sp: XeroXpmConectStoreProps) {
      this.sp = sp
      makeAutoObservable(this)
   }

   get connection() {
      return this.sp.xpmConnectionStore
   }

   get xeroCallbackUrl() {
      // Only `/xero-callback/app` and `/xero-callback/trial` are registered as redirect URIs
      // with Xero.
      const path = this.sp.originType === 'trial' ? 'trial' : 'app'
      return `${variables.regionBaseUrl}/xero-callback/${path}`
   }

   get xeroAuthorised() {
      return this.connection.xeroAuthorised
   }

   get xpmName() {
      return this.connection.xpmName
   }

   get isSyncing() {
      return this.connection.isSyncing
   }

   get integrationCardStatus() {
      return this.connection.cardStatus
   }

   /** True whenever the card must not offer an action — fetching, exchanging, or leaving for Xero. */
   get busy() {
      return this.loader.isLoading || this.connection.busy || this.redirecting
   }

   /** Fetches the consent URL when the org isn't connected, unless an exchange is already running. */
   initialiseSettings = async () => {
      if (this.connection.busy) return
      await this.loader.executeLoading(async () => {
         try {
            if (this.connection.xeroAuthorised) return
            this.consent = await apiClient.xpm.getConsentUrl.query({ xeroCallbackUrl: this.xeroCallbackUrl })
         } catch (error) {
            console.log(error)
         }
      })
   }

   connect = async () => {
      this.redirecting = true
      // The consent fetch on mount can fail silently, leaving nothing to redirect to.
      if (!this.consent) {
         await this.initialiseSettings()
      }
      sessionStorage.setItem(XPM_RETURN_PATH_KEY, window.location.pathname + window.location.search)
      sessionStorage.setItem(XPM_CALLBACK_URL_KEY, this.xeroCallbackUrl)
      window.location.href = notNull(this.consent).url
   }

   syncNow = async () => {
      await this.connection.runSync()
   }

   disconnect = async () => {
      try {
         this.connection.setDisconnecting()
         await apiClient.xpm.disconnect.mutate({})
         this.connection.setDisconnected()
         this.consent = await apiClient.xpm.getConsentUrl.query({ xeroCallbackUrl: this.xeroCallbackUrl })
      } catch (error: unknown) {
         this.connection.setDisconnectFailed()
         Sentry.captureException(error, {
            tags: { component: 'XeroXpmConnectStore', action: 'disconnect' },
         })
         snackbarError('We had trouble disconnecting from XPM. Please try again.')
      }
   }
}

export const { Provider: XeroXpmConectStoreProvider, useProvidedStore: useXeroXpmConectStore } = createStoreContext(XeroXpmConectStore)
